import type { LucideIcon } from 'lucide-react';
import { PanelError } from '../../components/PanelError.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey, TParams } from '../../i18n/index.ts';

/**
 * The pieces `/usage` renders on BOTH sides of the permission fork.
 *
 * `AdminUsage` and `MemberUsage` are separate screens with separate
 * data sources, but an operator who can see both must not perceive
 * them as two products: the range picker has to behave the same, a KPI
 * tile has to be the same tile, and a failed query has to fail the
 * same way. Anything used by exactly one side deliberately does NOT
 * live here — it stays next to its only caller so the split keeps
 * telling the truth about who depends on what.
 *
 * These are presentational only. Range STATE and the queries built
 * from it belong to each page; this module just draws.
 */

/**
 * Inclusive span cap for the calendar range filter, matching the
 * platform-api admin-dashboard hard cap. It lives here rather than on
 * one page so the admin and member windows cannot cap differently — a
 * custom range a member can pick must be one an admin can pick too.
 */
export const MAX_RANGE_SPAN_DAYS = 90;

/**
 * The catalog lookup `useT()` hands back, named so the range-preset
 * builders on both pages can stay plain functions taking it as an
 * argument instead of becoming hooks.
 */
export type Translate = (key: I18nKey, params?: TParams) => string;

/**
 * One KPI tile in a page's summary grid. The TILE is shared so the two
 * pages cannot draw a headline number two ways; how many of them a page
 * lays out is the page's own business, and the two differ — an admin
 * has org-wide seat and key counts a member has no version of. That is
 * also why the tile takes plain display strings: the caller has already
 * resolved its own numbers and placeholders.
 */
export function SummaryCard(props: {
  readonly label: string;
  readonly value: string;
  readonly detail: string;
  readonly icon: LucideIcon;
  readonly testId: string;
}): JSX.Element {
  const IconGlyph = props.icon;
  return (
    <article
      data-testid={props.testId}
      className="flex min-h-32 flex-col justify-between gap-4 rounded-lg border border-line bg-card p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-caption font-medium text-ink-mid">{props.label}</span>
        <span
          className="rounded-md border border-line-soft bg-raised p-1.5 text-ink-low"
          aria-hidden="true"
        >
          <IconGlyph className="h-4 w-4" />
        </span>
      </div>
      <span className="font-mono text-stat text-ink">{props.value}</span>
      <span className="text-caption text-ink-low">{props.detail}</span>
    </article>
  );
}

/** Title/subtitle bar for a panel, with an optional right-aligned control. */
export function PanelHeader(props: {
  readonly title: string;
  readonly subtitle?: string;
  readonly action?: JSX.Element;
}): JSX.Element {
  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex flex-col gap-1">
        <h3 className="text-heading text-ink">{props.title}</h3>
        {props.subtitle !== undefined ? (
          <p className="text-caption text-ink-low">{props.subtitle}</p>
        ) : null}
      </div>
      {props.action !== undefined ? <div className="shrink-0">{props.action}</div> : null}
    </header>
  );
}

/**
 * Query failure surface: ONE generic line plus a retry, never the
 * error's own text.
 *
 * This deliberately does not render `PlatformApiError.code` or
 * `.message`. Those are upstream-shaped — a tenant reading
 * `ROUTE_UPSTREAM_UNAVAILABLE` learns something about routing that the
 * tenant surface is not allowed to tell them (AGENTS.md: 租户对上游
 * 路由是黑盒), and a support thread starts from the request id in the
 * network layer, not from a string on the page.
 *
 * Retry is inline and toast-free: an async load failure reports once,
 * in the place that failed. `testId` is per call site so a test can
 * name which query failed.
 *
 * The sentence and its retry label are resolved HERE rather than taken
 * as props, so a panel-local failure and a page-level one cannot word
 * the same event two ways.
 */
export function UsageError({
  onRetry,
  testId,
}: {
  readonly onRetry: () => void;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  return (
    <div className="rounded-lg border border-line bg-card p-5">
      <PanelError
        message={t('usage.error.load')}
        retryLabel={t('usage.retry')}
        onRetry={onRetry}
        testId={testId}
      />
    </div>
  );
}
